import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../../../../../services/api";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import SyncLoader from "react-spinners/SyncLoader";
import * as Dialog from "@radix-ui/react-dialog";
import { format } from "date-fns";
import { useAuth } from "../../../../../../hooks/auth";
import { debounce } from "lodash";
import { useRoutes } from "../../../../../../hooks/routes";

import IHookForm from "../../../../../../shared/dtos/IHookForm";
import IListUsersAdminEditorApiProps from "./dtos/IListUsersAdminEditorApiProps";
import IListUsersAdminEditorDataParams from "./dtos/IListUsersAdminEditorDataParams";
import IFormSearch from "./dtos/IFormSearch";

import MasterAsideControls from "../../../../components/SidebarMaster/MasterAsideControls";
import HeaderMaster from "../../../../components/SidebarMaster/HeaderMaster";
import MasterContainer from "../../../../components/SidebarMaster/MasterContainer";
import MasterMain from "../../../../components/SidebarMaster/MasterMain";

import DialogModal from "../../../../../../shared/components/DialogModal";
import Button from "../../../../../../shared/components/Button";
import Input from "../../../../../../shared/components/Input";
import IconSvg from "../../../../../../shared/components/IconSvg";
import Pagination from "../../../../../../shared/components/Pagination";
import Tooltip from "../../../../../../shared/components/Tooltip";

import trashIcon from "../../../../../../shared/assets/trash-icon.svg";
import iconSearchWhite from "../../../../../../shared/assets/icon-search-white.svg";

import { Notify } from "../../../../../../shared/utils";

import {
  BoxList,
  ButtonFlexBox,
  ButtonSearch,
  FlexBox,
  HStack,
  Main,
  Form,
  ContainerCreate,
  FlexPagination,
  ContainerLoading,
  Table,
  TableTh,
  TableTr,
  TableTd,
  TableThead,
  TableTbody,
  TableTdText,
} from "./styles";

const ListUsersAdminEditor: React.FC = () => {
  const location = useLocation();
  const dataLocation: any = location.state;

  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [dataUsersAdminEditorList, setDataUsersAdminEditorList] =
    useState<IListUsersAdminEditorApiProps>({
      pagination: 0,
      dataArray: [],
    });
  const [listUsersAdminEditorDataParams, setListUsersAdminEditorDataParams] =
    useState<IListUsersAdminEditorDataParams>({
      pagination:
        dataLocation && dataLocation.params != null
          ? dataLocation.params.pagination
          : 0,
    });

  const { data: dataUseAuth } = useAuth();

  const { getUrl } = useRoutes();

  const userPermissionCreate =
    dataUseAuth.admin_user_permissions.create_users_admin_editor;
  const userPermissionRead =
    dataUseAuth.admin_user_permissions.read_users_admin_editor;
  const userPermissionDelete =
    dataUseAuth.admin_user_permissions.delete_users_admin_editor;

  const navigate = useNavigate();

  useEffect(() => {
    getUrl("/users-admin-editor");
  }, []);

  const handleCreateUsersAdminEditor = useCallback(() => {
    navigate("/create-user-admin-editor", {
      state: { params: listUsersAdminEditorDataParams },
    });
  }, [navigate, listUsersAdminEditorDataParams]);

  useEffect(() => {
    (async () => {
      const response = await api.get("/bits-templates-editor-users", {
        params: listUsersAdminEditorDataParams,
      });

      setDataUsersAdminEditorList(response.data);
      setTotalPages(response.data.pagination);
      setLoading(false);
    })();
  }, [reload, listUsersAdminEditorDataParams]);

  const handleDeleteUsersAdminEditor = useCallback(
    async (id: string) => {
      try {
        await api.delete(`/bits-templates-editor-users/${id}`);
        setReload(!reload);
      } catch (error: any) {
        console.log(error);
      }
    },
    [reload]
  );

  const formSchema = yup.object().shape({
    search: yup.string(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IHookForm>({
    mode: "onChange",
    resolver: yupResolver(formSchema),
  });

  const onSubmit = useCallback(
    debounce((formData: string) => {
      try {
        setLoading(true);
        if (formData === "") {
          setListUsersAdminEditorDataParams({
            pagination: 0,
          });
        } else {
          setListUsersAdminEditorDataParams({
            pagination: 0,
            search: formData,
          });
        }
      } catch (err: any) {
        console.log(err?.response?.data);
      }
    }, 400),
    []
  );

  const navigateView = useCallback(
    (item) => {
      navigate(`/view-user-admin/user-admin-editor/${item.user_id}`, {
        state: { params: listUsersAdminEditorDataParams },
      });
    },
    [navigate, listUsersAdminEditorDataParams]
  );

  const notifyRead = useCallback(() => {
    Notify({ text: "Você não tem permissão de visualizar", type: "error" });
  }, []);

  const handleInputSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const searchTerm = event.target.value;

      onSubmit(searchTerm);
    },
    [onSubmit]
  );

  return (
    <MasterContainer>
      <MasterAsideControls />
      <HeaderMaster namePage="Listar usuários editores admin" />
      <MasterMain>
        <Main>
          <ContainerCreate>
            <Button
              onClick={handleCreateUsersAdminEditor}
              disabled={!userPermissionCreate}
            >
              Criar usuário editor admin
            </Button>
          </ContainerCreate>

          <HStack>
            <Input
              onChangeInput={handleInputSearchChange}
              label="search"
              register={register}
              required={true}
              placeholder="Pesquisar"
              style={{ width: "100%" }}
            />
          </HStack>

          <BoxList>
            {loading && (
              <ContainerLoading>
                <SyncLoader loading={loading} color="#663399" size={10} />
              </ContainerLoading>
            )}

            {!loading && (
              <Table>
                <TableThead>
                  <TableTr>
                    <TableTh>Nome</TableTh>
                    <TableTh>Sobrenome</TableTh>
                    <TableTh>E-mail</TableTh>
                    <TableTh>Criado em</TableTh>
                    <TableTh>Opções</TableTh>
                  </TableTr>
                </TableThead>

                <TableTbody>
                  {dataUsersAdminEditorList.dataArray.map((item, index) => (
                    <TableTr key={item.user_id}>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <TableTdText>{item.first_name}</TableTdText>
                      </TableTd>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <TableTdText>{item.last_name}</TableTdText>
                      </TableTd>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <TableTdText>{item.email}</TableTdText>
                      </TableTd>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <TableTdText>
                          {format(
                            new Date(item.created_at),
                            "dd/MM/yyyy HH:mm"
                          )}
                        </TableTdText>
                      </TableTd>

                      <TableTd>
                        <FlexBox>
                          <Dialog.Root>
                            <Tooltip title="Deletar usuário" placement="top">
                              <Dialog.Trigger asChild>
                                <ButtonFlexBox disabled={!userPermissionDelete}>
                                  <IconSvg
                                    width="18"
                                    height="18"
                                    image={trashIcon}
                                  />
                                </ButtonFlexBox>
                              </Dialog.Trigger>
                            </Tooltip>

                            <DialogModal
                              title="Remover permissão de editor"
                              subtitle={`${item.first_name} ${item.last_name}`}
                              type="delete"
                              callback={() =>
                                handleDeleteUsersAdminEditor(item.user_id)
                              }
                            />
                          </Dialog.Root>
                        </FlexBox>
                      </TableTd>
                    </TableTr>
                  ))}
                </TableTbody>
              </Table>
            )}
          </BoxList>
          <FlexPagination>
            {dataUsersAdminEditorList.dataArray.length > 0 && (
              <Pagination
                total={totalPages}
                offset={listUsersAdminEditorDataParams.pagination}
                setDataParams={setListUsersAdminEditorDataParams}
                dataParams={listUsersAdminEditorDataParams}
              />
            )}
          </FlexPagination>
        </Main>
      </MasterMain>
    </MasterContainer>
  );
};

export default ListUsersAdminEditor;
